import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { InTulsaActivityPoint, InTulsaAllTime } from '../types';
import { latest, pctChange, previous, yearOf } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type PresentationsCardProps = {
  series: InTulsaActivityPoint[];
  allTime: InTulsaAllTime;
};

export function PresentationsCard({ series, allTime }: PresentationsCardProps) {
  const theme = useMantineTheme();
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const qoq = cur ? pctChange(cur.presentationsDelivered, prev?.presentationsDelivered ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.presentationsDelivered, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.presentationsDelivered, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const allTimeTotal = allTime.presentationsDelivered;

  const status = { label: 'Snapshot', color: 'gray' as const };

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const annualTotals = series.reduce<Record<number, number>>((acc, point) => {
    const year = yearOf(point.quarter);
    acc[year] = (acc[year] ?? 0) + point.presentationsDelivered;
    return acc;
  }, {});

  const dataYears = Object.keys(annualTotals)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year));
  const minYear = dataYears.length > 0 ? Math.min(...dataYears) : yearOf(cur?.quarter ?? '');
  const maxYear = dataYears.length > 0 ? Math.max(...dataYears) : yearOf(cur?.quarter ?? '');
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, idx) => minYear + idx);
  const chartTotal = years.reduce((sum, year) => sum + (annualTotals[year] ?? 0), 0);
  const priorToRange = Math.max(0, allTimeTotal - chartTotal);

  const annualData = years.map((year) => ({
    year,
    yearLabel: String(year),
    total: (annualTotals[year] ?? 0) + (year === minYear ? priorToRange : 0),
  }));

  return (
    <CardShell
      title="Presentations Delivered"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Presentations delivered this quarter (at request of Workforce Intermediary).',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.presentationsDelivered ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date presentations delivered (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total presentations delivered across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {allTimeTotal}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2 }}>
          Annual Presentations Delivered
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="yearLabel" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? v : '—'} Presentations
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="total"
                fill={theme.colors.blue[6]}
                  barSize={18}
                activeBar={{ fill: theme.colors.blue[5] }}
                radius={[8, 8, 8, 8]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
